import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { CircularProgressWithLabel } from "components";
import { Alert, Button, CircularProgress, TextField } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import FileOpenOutlinedIcon from "@mui/icons-material/FileOpenOutlined";
import { ProcessState } from "types/process";
import { RulesEngineApiUrl } from "api/rulesEngine/constants";
import { UploadProductLineRulesZip } from "api/rulesEngine/api";

export const UploadRuleEngineJsonForm = ({
  selectedAction,
  visible,
}: {
  selectedAction: number;
  visible: boolean;
}) => {
  //State
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileJson, setFileJson] = useState<Record<string, unknown> | null>(
    null
  );
  const [fileName, setFileName] = useState<string>("");
  const [productLineName, setProductLineName] = useState<string>("");
  const [processMsg, setProcessMessage] = useState<string>("");
  const [processPercentComplete, setProcessPercentComplete] =
    useState<number>(0);
  const [processState, setProcessState] = useState<ProcessState>(
    ProcessState.IDLE
  );
  const [rulesEnvironmentName, setRulesEnvironmentName] =
    useState<string>("Sandbox");
  const [rulesEngineURL, setRulesEngineURL] = useState<RulesEngineApiUrl>(
    RulesEngineApiUrl.SANDBOX
  );

  useEffect(() => {
    if (!visible) {
      resetAllState();
    }
  }, [visible]);

  useEffect(() => {
    //Determine the name of the targeted rules environment and its URL
    switch (selectedAction) {
      case 0:
        setRulesEnvironmentName("Sandbox");
        setRulesEngineURL(RulesEngineApiUrl.SANDBOX);
        break;
      case 1:
        setRulesEnvironmentName("Dev");
        setRulesEngineURL(RulesEngineApiUrl.DEV);
        break;
      default:
        setRulesEnvironmentName("Sandbox");
        setRulesEngineURL(RulesEngineApiUrl.SANDBOX);
        break;
    }
  }, [selectedAction]);

  const clearProductLineInfo = () => {
    setFileJson(null);
    setProductLineName("");
    setProcessState(ProcessState.IDLE);
    setProcessPercentComplete(0);
    setProcessMessage("");
  };

  const resetAllState = () => {
    setFileError(null);
    setFileJson(null);
    setFileName("");
    setProductLineName("");
    setProcessMessage("");
    setProcessPercentComplete(0);
    setProcessState(ProcessState.IDLE);
    clearProductLineInfo;
    setFileJson(null);
  };

  //Determine if we have gathered all the items necessary to do the import
  const isUploadReady =
    processState === (ProcessState.IDLE || ProcessState.ERROR) &&
    fileJson &&
    productLineName;

  // handlers
  const handleFileChange = (event) => {
    //Clear out any previous productline data
    clearProductLineInfo();

    //Get a reference to the file object from the input component
    const file = event.target.files && event.target.files[0];
    if (!file) {
      setFileName("");
      setFileError("No import file chosen");
      return;
    }

    //reset file input
    event.target.value = null;

    //Save the file to state
    setFileName(file.name);

    handleFileChosen(file);
  };

  const handleFileChosen = (file: File | null) => {
    if (!file) {
      setFileError("No import file chosen");
      clearProductLineInfo();
      return;
    }
    if (!file.type.match("json")) {
      setFileError("Import file is not valid JSON.");
      clearProductLineInfo();
      return;
    }
    //Case insensitive match for rules engine json file name suffix
    if (!file.name || !/@@002.*\.json/i.test(file.name)) {
      setFileError("Not a rules engine json file");
      clearProductLineInfo();
      return;
    }

    const fileReader = new FileReader();
    fileReader.onloadend = handleFileRead;
    fileReader.readAsText(file);
  };

  const handleFileRead = (e: ProgressEvent<FileReader>) => {
    const content = e.target?.result as string;
    let parsedData: any = null;
    try {
      parsedData = JSON.parse(content);
      setFileJson(parsedData);
      setFileError(null);
    } catch (err) {
      setFileError("Import file is not valid JSON.");
      clearProductLineInfo();
      return;
    }

    if (!parsedData?.Name) {
      setFileError(
        "Unable to determine the product line name.  This is not a valid file for import."
      );
      clearProductLineInfo();
      return;
    }

    //Read the object to extract product line info from it and save to state.
    setProductLineName(parsedData?.Name);
  };

  const handleUploadButtonClick = () => {
    doAction();
  };

  const doAction = async () => {
    if (!fileJson) return;

    setProcessState(ProcessState.PROCESSING);
    setProcessMessage(`Zipping and uploading`);

    try {
      await UploadProductLineRulesZip(rulesEngineURL, fileName, fileJson)
        .then((response) => {
          const msg = `'${fileName}' uploaded to ${rulesEnvironmentName} Rules Engine`;
          setProcessMessage(msg);
          setProcessPercentComplete(100);
          console.log(msg, response);
          toast.success(msg);
        })
        .catch((error) => {
          const errMsg = `'${fileName}' upload to ${rulesEnvironmentName} Rules Engine failed`;
          setProcessMessage(errMsg);
          setProcessPercentComplete(100);
          console.error(errMsg, error);
          toast.error(errMsg);
        });
    } catch (e) {
      const errMsg = `Error while zipping and uploading '${fileName}' to ${rulesEnvironmentName} Rules Engine`;
      console.error(e, errMsg);
      setProcessMessage(errMsg);
      setProcessPercentComplete(100);
    }

    setProcessState(ProcessState.COMPLETE);
  };

  //If the form is not visible, don't bother rendering anything
  if (!visible) {
    return null;
  }

  return (
    <>
      <Grid
        container
        columnSpacing={1}
        rowSpacing={3}
        alignItems="center"
        style={{ marginLeft: "20px", marginRight: "20px" }}
      >
        <Grid xs={10}>
          <TextField
            label="File Name"
            value={fileName}
            id="file-text-input"
            variant="outlined"
            size="small"
            fullWidth
            multiline
            margin="none"
            disabled
          />
        </Grid>
        <Grid xs>
          <Button component="label" variant="contained">
            <FileOpenOutlinedIcon />
            <VisuallyHiddenInput
              type="file"
              accept=".json"
              onChange={handleFileChange}
            />
          </Button>
        </Grid>
        {fileError && (
          <Grid xs={12}>
            <Alert severity="error">{fileError}</Alert>
          </Grid>
        )}
        <Grid xs={12}>
          <TextField
            label="Product Line Name"
            value={productLineName}
            id="productline-name"
            variant="outlined"
            size="small"
            disabled
            fullWidth
            margin="none"
          />
        </Grid>
        <Grid xs={12}>
          {isUploadReady && (
            <Alert severity="warning">
              You are about to upload product line &quot;{productLineName}
              &quot; to the&nbsp;
              {rulesEnvironmentName} rules engine.
              <p>
                <strong>
                  Clicking the upload button below will overwrite any previous
                  version of the product line.
                </strong>
              </p>
            </Alert>
          )}
        </Grid>
        <Grid xs={12}>
          <Button
            id="productline-upload"
            variant="contained"
            size="large"
            fullWidth
            disabled={!isUploadReady}
            onClick={handleUploadButtonClick}
          >
            Upload Product Line to {rulesEnvironmentName}
          </Button>
        </Grid>

        <Grid xs={12} display="flex" justifyContent="center">
          {(processState === ProcessState.INITIALIZING ||
            processState === ProcessState.QUEUED ||
            processState === ProcessState.PROCESSING) && <CircularProgress />}
          {processState === ProcessState.COMPLETE && (
            <CircularProgressWithLabel value={processPercentComplete} />
          )}
        </Grid>
        <Grid xs={12} display="flex" justifyContent="center">
          {(processState === ProcessState.INITIALIZING ||
            processState === ProcessState.QUEUED ||
            processState === ProcessState.PROCESSING) && (
            <Alert severity="info" variant="outlined">
              <strong>Progress</strong>
              <p>{processMsg}</p>
            </Alert>
          )}
          {processState === ProcessState.COMPLETE && (
            <Alert severity="success" variant="outlined">
              <strong>Success</strong>
              <p>{processMsg}</p>
            </Alert>
          )}
          {processState === ProcessState.ERROR && (
            <Alert severity="error" variant="outlined">
              <strong>Error: Upload Failed</strong>
              <p>{processMsg}</p>
            </Alert>
          )}
        </Grid>
      </Grid>
    </>
  );
};

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default UploadRuleEngineJsonForm;
